import type {
  EntityOrSuggestionRef,
  EntitySuggestion,
  OrgFunction,
  OrgFunctionKind,
  PopulatedDepartment,
  System
} from '@bliks-io/contracts';

// The prefix is intentionally distinctive — a real ObjectId can never start
// with double-underscore sentinels, so the synthetic id can travel through
// the picker's id-as-string interfaces without collision and round-trip back
// into a suggestion ref at confirm time.
const PENDING_PREFIX = '__pending__:';

export function toPendingId(suggestionId: string): string {
  return `${PENDING_PREFIX}${suggestionId}`;
}

export function fromPendingId(id: string): string | null {
  return id.startsWith(PENDING_PREFIX) ? id.slice(PENDING_PREFIX.length) : null;
}

export function isPendingId(id: string): boolean {
  return id.startsWith(PENDING_PREFIX);
}

/**
 * Unwrap a picker id back into a slot ref: a prefixed id becomes a suggestion
 * ref (the slot rewrites it to a real entity ref once the suggestion is
 * approved), anything else an entity ref.
 */
export function toEntityOrSuggestionRef(id: string): EntityOrSuggestionRef {
  const suggestionId = fromPendingId(id);
  return suggestionId ? { type: 'suggestion', id: suggestionId } : { type: 'entity', id };
}

/**
 * Synthesize `System` items from pending system suggestions so the picker
 * surfaces them as ordinary, selectable rows. Fields the picker doesn't read
 * (e.g. `systemOwner`) get placeholder values that satisfy the type.
 */
export function buildPendingSystemItems(suggestions: EntitySuggestion[] | undefined): System[] {
  if (!suggestions) return [];
  return suggestions
    .filter(
      (s): s is Extract<EntitySuggestion, { kind: 'system' }> =>
        s.kind === 'system' && s.status === 'pending'
    )
    .map((s) => ({
      _id: toPendingId(s._id),
      name: s.payload.name,
      description: s.payload.description,
      category: s.payload.category,
      url: s.payload.url,
      licenceSeats: s.payload.licenceSeats,
      // SystemSchema requires `systemOwner` — synthetic rows aren't rendered
      // by SystemCard at a depth that touches it, so a placeholder is fine.
      systemOwner: ''
    }));
}

/**
 * Synthesize `OrgFunction` items from pending orgFunction suggestions so the picker
 * surfaces them as ordinary, selectable rows alongside resolved OrgFunctions. We
 * shape only what the renderer reads (name, kind, optional department name) and
 * stub the rest. `department` is optional on a OrgFunction suggestion (a role, or
 * a dept-less team, carries none). `kind` narrows the ghost rows to the
 * payload kind the (team- or role-fixed) picker page shows; omit it on a
 * surface that offers both kinds.
 */
export function buildPendingOrgFunctionItems(
  suggestions: EntitySuggestion[] | undefined,
  kind?: OrgFunctionKind
): OrgFunction[] {
  if (!suggestions) return [];
  return suggestions
    .filter(
      (s): s is Extract<EntitySuggestion, { kind: 'orgFunction' }> =>
        s.kind === 'orgFunction' && s.status === 'pending' && (!kind || s.payload.kind === kind)
    )
    .map((s) => {
      const department: PopulatedDepartment | undefined = s.payload.department
        ? ({
            _id: `__pending-dept__:${s._id}`,
            name: s.payload.department
          } as PopulatedDepartment)
        : undefined;
      return {
        _id: toPendingId(s._id),
        name: s.payload.name,
        kind: s.payload.kind,
        email: s.payload.email ?? '',
        description: s.payload.description,
        department
      } as OrgFunction;
    });
}

export interface PartitionedConfirmedIds {
  realIds: string[];
  /** Suggestion ids that were preselected AND remained checked at confirm. */
  keptSuggestionIds: string[];
  /** Suggestion ids the user newly checked (weren't in `preselectedSuggestionIds`). */
  addedSuggestionIds: string[];
  /** Suggestion ids the user unchecked (were in `preselectedSuggestionIds` but missing from confirm). */
  removedSuggestionIds: string[];
}

/**
 * Split the picker's confirmed id list into real entity ids and three
 * suggestion outcomes (kept / added / removed). Consumers use the partition to
 * rebuild the slot as a mixed `EntityOrSuggestionRef[]`.
 *
 * `preselectedSuggestionIds` is the set of suggestion ids that were already
 * in the slot (and therefore prefixed inside `selectedIds` when the picker
 * was opened). Anything in confirm that doesn't match a preselected suggestion
 * is treated as a newly added one.
 */
export function partitionConfirmedIds(
  confirmedIds: string[],
  preselectedSuggestionIds: string[]
): PartitionedConfirmedIds {
  const preselectedSet = new Set(preselectedSuggestionIds);
  const confirmedSet = new Set(confirmedIds);
  const realIds: string[] = [];
  const keptSuggestionIds: string[] = [];
  const addedSuggestionIds: string[] = [];
  for (const id of confirmedIds) {
    const sid = fromPendingId(id);
    if (!sid) {
      realIds.push(id);
    } else if (preselectedSet.has(sid)) {
      keptSuggestionIds.push(sid);
    } else {
      addedSuggestionIds.push(sid);
    }
  }
  const removedSuggestionIds: string[] = [];
  for (const sid of preselectedSuggestionIds) {
    if (!confirmedSet.has(toPendingId(sid))) {
      removedSuggestionIds.push(sid);
    }
  }
  return {
    realIds,
    keptSuggestionIds,
    addedSuggestionIds,
    removedSuggestionIds
  };
}
